import { Box, BoxProps } from "@chakra-ui/react";
import { motion, MotionProps, useScroll, useSpring } from "framer-motion";

export const MotionBox = motion(Box);
export const MotionFlex = motion(Box);

type RevealProps = BoxProps &
  MotionProps & {
    delay?: number;
  };

const easeOut = [0.22, 1, 0.36, 1] as const;

export const Reveal = ({ children, delay = 0, ...rest }: RevealProps) => (
  <MotionBox
    initial={{ opacity: 0, y: 32 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, margin: "-80px" }}
    transition={{ duration: 0.7, delay, ease: easeOut }}
    {...rest}
  >
    {children}
  </MotionBox>
);

// Thin lime progress bar pinned to the top of the viewport, filling as the
// visitor scrolls through the page — a small "alive" motion cue used across
// the whole site rather than just inside one section.
export const ScrollProgressBar = ({ color = "lime.500" }: { color?: string }) => {
  const { scrollYProgress } = useScroll();
  const scaleX = useSpring(scrollYProgress, {
    stiffness: 260,
    damping: 30,
    restDelta: 0.001,
  });

  return (
    <MotionBox
      position="fixed"
      top={0}
      left={0}
      right={0}
      height="3px"
      bg={color}
      transformOrigin="0%"
      zIndex={200}
      style={{ scaleX }}
    />
  );
};

// A soft cluster of blurred, continuously-drifting color blobs used as a
// "live" background — animates on its own timeline (not tied to scroll) so
// the page never feels static, while staying subtle enough to sit behind copy.
type LiveBackgroundProps = { variant?: "hero" | "panel"; tone?: "lime" | "teal" | "wobble" | "rosa" };

const BLOB_COLORS = {
  lime: ["rgba(159, 226, 58, 0.35)", "rgba(182, 238, 85, 0.3)"],
  teal: ["rgba(20, 184, 166, 0.3)", "rgba(94, 234, 212, 0.28)"],
  // Wobble's actual brand teal (#007188 / #5AB3C5), not BOB's teal.
  wobble: ["rgba(0, 113, 136, 0.28)", "rgba(90, 179, 197, 0.3)"],
  // Rosa's indigo/violet accent (#6D28D9 / #6366F1).
  rosa: ["rgba(109, 40, 217, 0.22)", "rgba(99, 102, 241, 0.24)"],
};

export const LiveBackground = ({ variant = "hero", tone = "lime" }: LiveBackgroundProps) => {
  const size = variant === "hero" ? "620px" : "420px";
  const [first, second] = BLOB_COLORS[tone];
  return (
    <Box position="absolute" inset={0} overflow="hidden" zIndex={0} pointerEvents="none">
      <MotionBox
        position="absolute"
        top="-15%"
        left="-10%"
        w={size}
        h={size}
        borderRadius="50%"
        bg={`radial-gradient(closest-side, ${first}, transparent 72%)`}
        filter="blur(10px)"
        animate={{
          x: [0, 40, -20, 0],
          y: [0, 30, 60, 0],
        }}
        transition={{ duration: 22, repeat: Infinity, ease: "easeInOut" }}
      />
      <MotionBox
        position="absolute"
        bottom="-20%"
        right="-10%"
        w={size}
        h={size}
        borderRadius="50%"
        bg={`radial-gradient(closest-side, ${second}, transparent 72%)`}
        filter="blur(10px)"
        animate={{
          x: [0, -30, 20, 0],
          y: [0, -40, -10, 0],
        }}
        transition={{ duration: 26, repeat: Infinity, ease: "easeInOut" }}
      />
    </Box>
  );
};

export default Reveal;
